import { useCallback, useState } from 'react';
import FormGroup from '~/components/Form/FormGroup';
import Alert from '~/components/General/Alert';
import Button from '~/components/General/Button';
import Modal from '~/components/General/Modal';
import usePackageSessionQuotation from '~/hooks/bookingFlow/usePackageSessionQuotation';
import packageSessionQuotationStyles from '~/styles/bookingFlow/package-session-quotation.module.css';
import { useOptions } from '~/hooks/useOptions';
import useTranslation from '~/hooks/useTranslation';
import { PackageSessionQuotationRequest } from '~/types/api';
import { InferredBookingFlowOptions } from '~/types/options';
import PlainTextField from '~/components/Form/PlainTextField';
import IconMail from '~icons/tabler/mail';

type Props = {
  sessionId: string;
  className?: string;
};

export default function QuotationForm({
  sessionId,
  className,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const options = useOptions() as InferredBookingFlowOptions;
  const translate = useTranslation();

  const packageSessionQuotationMutation = usePackageSessionQuotation();

  const [formData, setFormData] = useState<PackageSessionQuotationRequest>({
    order_session_id: sessionId,
    office_id: options.officeId,
    department_id: options.departmentId,
    locale: options.locale,
    url: window.location.href,
    email: '',
    name: '',
    message: '',
  });

  const onOpenModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const onSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    packageSessionQuotationMutation.mutate(formData);
  }, [packageSessionQuotationMutation, formData]);

  const onInputChange = useCallback((key: keyof PackageSessionQuotationRequest, value: string) => {
    setFormData({ ...formData, [key]: value });
  }, [formData]);

  return (
    <div className={className}>
      <Button
        onClick={onOpenModal}
        type="button"
        styleType="outlineSecondary"
        className={packageSessionQuotationStyles.quotationFormButton}
      >
        <IconMail className={packageSessionQuotationStyles.quotationFormIcon} />
        <span>
          {translate('bookingFlow.overview.quotation_button')}
        </span>
      </Button>

      <Modal
        title={translate('bookingFlow.overview.quotation_modal_title')}
        description={translate('bookingFlow.overview.quotation_modal_description')}
        isOpen={modalOpen}
        onClose={onCloseModal}
      >
        {
          packageSessionQuotationMutation.isSuccess
            ? (
              <div className={packageSessionQuotationStyles.packageSessionQuotationSuccess}>
                {translate('bookingFlow.overview.quotation_modal_success')}
              </div>
            )
            : (
              <>
                <Alert
                  type="danger"
                  error={packageSessionQuotationMutation.error}
                />

                <form method="GET" action="" onSubmit={onSubmit}>
                  <FormGroup>
                    <PlainTextField
                      label={translate('form.name')}
                      value={formData.name}
                      onChange={(event) => onInputChange('name', event.target.value)}
                      max={200}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <PlainTextField
                      label={translate('form.email')}
                      fieldType="email"
                      value={formData.email}
                      onChange={(event) => onInputChange('email', event.target.value)}
                      max={200}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <PlainTextField
                      label={translate('form.message')}
                      value={formData.message ?? ''}
                      onChange={(event) => onInputChange('message', event.target.value)}
                      rows={3}
                      max={1000}
                    />
                  </FormGroup>

                  <Button
                    type="submit"
                    styleType="primary"
                    disabled={packageSessionQuotationMutation.isPending}
                  >
                    {translate('bookingFlow.overview.quotation_modal_submit')}
                  </Button>
                </form>
              </>
            )
        }
      </Modal>
    </div>
  );
}
